import { Dispatch, useState } from 'react';
import { Modal, Button, Alert, Spinner} from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import { IAgent } from '../../models/agent';
import { deleteAgent } from '../../api/agent';
import { useNavigate, useParams } from 'react-router-dom';
import { FaTrashAlt, FaExclamationTriangle } from 'react-icons/fa';

type Props = {
    agent: IAgent;
    cancel: Dispatch<React.SetStateAction<boolean>>
    setError: Dispatch<React.SetStateAction<string>>
}

const ConfirmDeleteAgent = ({agent, cancel} : Props) => {
    const [error, setError] = useState<string | null>(null);
    const handleClose = () => cancel(false);
    const navigate = useNavigate();

    const queryClient = useQueryClient();
    const { mutateAsync: deleteAgentFn, isLoading} = useMutation(
        deleteAgent,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['Agents']);
                navigate(`/dashboard/orgs/${agent.organizationId}/`);
            },
            onError: (error) => {
                setError("Unexpected error. Please try again or contact admin.");
                handleClose();
            },
        }
    );

    const handleDelete = async () => {
        if(!agent) {
            setError("Agent not found.");
            handleClose();
            return;
        }
        deleteAgentFn({orgId: agent.organizationId, id: agent.id});
    }

    return (
        <Modal 
            show={!!agent} 
            onHide={handleClose} 
            centered
            contentClassName="bg-dark text-light"
        >
            <Modal.Header 
                closeButton 
                className="border-0 pb-0"
                style={{ backgroundColor: '#2d2d2d', borderBottom: '1px solid #444' }}
            >
                <Modal.Title className="d-flex align-items-center text-danger">
                    <FaExclamationTriangle className="me-2" />
                    Confirm Agent Deletion
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body className="pt-2" style={{ backgroundColor: '#2d2d2d' }}>
                <div className="d-flex align-items-start">
                    <div className="bg-danger bg-opacity-20 rounded p-3 me-3 mt-1">
                        <FaTrashAlt className="text-light" size={24} />
                    </div>
                    <div className="flex-grow-1">
                        <h6 className="mb-2 text-light">Are you absolutely sure?</h6>
                        <p style={{ color: '#ccc' }} className="mb-2">
                            This action will permanently delete the agent <strong className="text-light">"{agent.name}"</strong> and all associated data.
                        </p>
                        <div 
                            className="border border-danger rounded p-3 mt-3"
                            style={{ backgroundColor: '#2c1618', borderColor: '#dc3545' }}
                        >
                            <p className="text-danger mb-0 small fw-semibold">
                                ⚠️ This action cannot be undone. All uploaded data will be permanently lost.
                            </p>
                        </div>
                    </div>
                </div>

                {error && (
                    <Alert 
                        variant="danger" 
                        className="mt-3 mb-0"
                        style={{ backgroundColor: '#2c1618', borderColor: '#dc3545', color: '#f5c6cb' }}
                    >
                        <FaExclamationTriangle className="me-2" />
                        {error}
                    </Alert>
                )}
            </Modal.Body>
            
            <Modal.Footer 
                className="border-0 pt-0"
                style={{ backgroundColor: '#2d2d2d' }}
            >
                <Button 
                    variant="outline-secondary" 
                    onClick={handleClose} 
                    disabled={isLoading}
                    style={{ borderColor: '#555', color: '#ccc' }}
                >
                    Cancel
                </Button>
                <Button variant="danger" onClick={handleDelete} disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Deleting...
                        </>
                    ) : (
                        <>
                            <FaTrashAlt className="me-2" />
                            Delete Agent
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ConfirmDeleteAgent